// State a consumer supplies: a saved View, or a query and page it restores. The input uses the published
// shape (publish.js), and it may be partial; a field it omits keeps the table's current value. Unknown
// column keys are ignored, required columns stay visible, and widths are clamped to their column's bounds.

import { pruneFilters } from '../query/filters'
import { reconcileSort } from '../query/sort'

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const clampWidth = (column, width) => {
  if (typeof width !== 'number' || !Number.isFinite(width)) return null
  const size = column.size || {}
  let next = width
  if (typeof size.min === 'number') next = Math.max(size.min, next)
  if (typeof size.max === 'number') next = Math.min(size.max, next)
  return next
}

// Visible keys in the supplied order; columns the input leaves out keep their current position relative to
// the columns around them. A left-out column is hidden, except a required one. When the input also lists
// the columns it hid, a column it names in neither list was added since it was saved and keeps its
// configured visibility.
const viewFromColumns = (current, supplied, columns, savedHidden) => {
  const keys = columns.map(column => column.key)
  const required = columns.filter(c => c.required).map(c => c.key)
  const visible = supplied.filter(key => keys.includes(key))
  const unlisted = current.order.filter(
    key => keys.includes(key) && !visible.includes(key)
  )
  const hides = key =>
    !Array.isArray(savedHidden) ||
    savedHidden.includes(key) ||
    current.hidden.includes(key)
  const order = []
  let cursor = 0
  current.order.forEach(key => {
    if (!keys.includes(key)) return
    if (unlisted.includes(key)) order.push(key)
    else if (cursor < visible.length) order.push(visible[cursor++])
  })
  while (cursor < visible.length) order.push(visible[cursor++])
  keys.forEach(key => {
    if (!order.includes(key)) order.push(key)
  })
  return {
    order,
    hidden: unlisted.filter(key => !required.includes(key) && hides(key)),
  }
}

/**
 * Converts consumer-supplied state into the partial TableState the reducer's controlled merge applies.
 * @param {import('../types').TableState} state the current state
 * @param {object} input published-shape state, possibly partial
 * @param {import('../types').NormalizedConfig} config
 * @returns {object} a partial TableState
 */
export const fromControlled = (state, input, config) => {
  if (!input || typeof input !== 'object') return {}
  const columns = config.columns || []
  const keys = columns.map(column => column.key)
  const partial = {}
  const query = {}
  const view = {}

  if (
    !config.embedded &&
    has(input, 'search') &&
    typeof input.search === 'string'
  ) {
    query.search = input.search.trim()
    partial.searchDraft = input.search
  }
  if (!config.embedded && has(input, 'filters'))
    query.filters = pruneFilters(input.filters, { keys })
  if (
    !config.embedded &&
    has(input, 'sort') &&
    input.sort &&
    keys.includes(input.sort.key)
  )
    query.sort = { key: input.sort.key, direction: input.sort.direction }

  if (Array.isArray(input.columns))
    Object.assign(
      view,
      viewFromColumns(state.view, input.columns, columns, input.hidden)
    )
  if (input.widths && typeof input.widths === 'object') {
    const widths = {}
    Object.entries(input.widths).forEach(([key, width]) => {
      const column = columns.find(c => c.key === key)
      const clamped = column ? clampWidth(column, width) : null
      if (clamped !== null) widths[key] = clamped
    })
    view.widths = widths
  }
  if (input.density === 'comfortable' || input.density === 'compact')
    view.density = input.density

  if (Number.isInteger(input.pageSize) && input.pageSize > 0) {
    const allowed = config.pageSizes || []
    if (!allowed.length || allowed.includes(input.pageSize))
      partial.pageSize = input.pageSize
  }
  if (Number.isInteger(input.page) && input.page > 0) partial.page = input.page

  // a supplied hidden column cannot keep a filter or a user sort
  const hidden = view.hidden || state.view.hidden
  const filters = pruneFilters(
    has(query, 'filters') ? query.filters : state.query.filters,
    { keys, hidden }
  )
  if (has(query, 'filters') || view.hidden) query.filters = filters
  if (has(query, 'sort') || view.hidden)
    query.sort = reconcileSort(
      query.sort || state.query.sort,
      config.defaultSort,
      { keys, hidden }
    )

  if (Object.keys(query).length) partial.query = query
  if (Object.keys(view).length) partial.view = view
  return partial
}
